import React, { ComponentProps, FormEventHandler, forwardRef, MouseEventHandler, PropsWithChildren, ReactElement, ReactNode, useCallback } from 'react'
import ModalBase from './ModalBase'
import ModalBody from './ModalBody'
import ModalContent from './ModalContent'
import ModalFooter, { ModalFooterActionProps } from './ModalFooter'
import ModalHeader from './ModalHeader'
import ModalImageThumbnail from './ModalImageThumbnail'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { formToObject } from 'lib/forms/formToObject'

type InheritedModalHeaderProps = Pick<ComponentProps<typeof ModalHeader>, 'title' | 'subtitle'>
type InheritedModalFooterProps = Pick<ComponentProps<typeof ModalFooter>, 'isPrimaryActionHidden' | 'isSecondaryActionHidden'>
type InheritedModalImageThumbnailProps = Partial<Pick<ComponentProps<typeof ModalImageThumbnail>, 'image' | 'imageParams'>>
type InheritedModalContentProps = Pick<ComponentProps<typeof ModalContent>, 'flush'>

interface Props extends ComponentProps<typeof ModalBase>, InheritedModalHeaderProps, InheritedModalFooterProps, InheritedModalImageThumbnailProps, InheritedModalContentProps {
  isPrimaryActionDisabled?: boolean
  isSecondaryActionDisabled?: boolean
  /**
   * If provided, the primary action will be displayed.
   */
  primaryActionText?: string | ReactElement
  /**
   * If provided, the secondary action will be displayed.
   */
  secondaryActionText?: string | ReactElement
  /**
   * Sets the button type of the primary action.
   *
   * @default 'submit'
   *  */
  primaryActionType?: 'submit' | 'button'
  /**
   * Sets the button type of the secondary action.
   *  */
  secondaryActionType?: 'reset' | 'button'
  primaryActionHref?: string
  primaryActionTo?: string
  secondaryActionTo?: string
  /**
   * Extra node at the bottom of the header.
   */
  headerExtension?: ReactNode
  /**
   * `event.preventDefault()` is invoked.
   */
  onSubmit?: (e: React.FormEvent<HTMLFormElement>, resolve: (result?: any) => any) => void
  onReset?: FormEventHandler<HTMLFormElement>
  /**
   * Callback on back button click.
   *
   * If provided, enables the back button to be displayed.
   */
  onBack?: MouseEventHandler<HTMLButtonElement>
  onPrimaryActionClick?: ModalFooterActionProps['onClick']
  onSecondaryActionClick?: ModalFooterActionProps['onClick']
  formRef?: React.RefObject<HTMLFormElement | null>
}

const FormModal = forwardRef<HTMLDivElement, PropsWithChildren<Props>>((props, ref) => {
  const {
    children,
    image,
    imageParams,
    isPrimaryActionDisabled,
    isPrimaryActionHidden,
    isSecondaryActionDisabled,
    isSecondaryActionHidden,
    primaryActionText,
    primaryActionHref,
    primaryActionTo,
    secondaryActionText,
    primaryActionType = 'submit',
    secondaryActionType,
    subtitle,
    title,
    onSubmit,
    onReset,
    onBack,
    onClose,
    onPrimaryActionClick,
    onSecondaryActionClick,
    formRef,
    flush,
    'data-testid': dataTestId,
    ...rest
  } = props

  const { resolve } = useModalElementContext<any>()

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    if (onSubmit) {
      onSubmit?.(event, resolve)
    } else {
      const formValues = formToObject(event.currentTarget)
      resolve(formValues)
    }
  }, [onSubmit, resolve])

  return <ModalBase {...rest} data-testid={dataTestId} onClose={onClose}>
    <form
      ref={formRef}
      onSubmit={handleSubmission}
      onReset={onReset}
    >
      <ModalHeader
        title={title}
        subtitle={subtitle}
        onBackButtonClick={onBack}
        onCloseButtonClick={onClose}
        data-testid={dataTestId ? `${dataTestId}-header` : undefined}
      />
      <ModalBody ref={ref}>
        {image && <ModalImageThumbnail image={image} imageParams={imageParams} />}
        {!!children && <ModalContent flush={flush}>
          {children}
        </ModalContent>}
      </ModalBody>
      {(primaryActionText || secondaryActionText) && <ModalFooter
        data-testid={dataTestId ? `${dataTestId}-footer` : undefined}
        isPrimaryActionHidden={isPrimaryActionHidden}
        isSecondaryActionHidden={isSecondaryActionHidden}
        primaryActionProps={{
          type: primaryActionType,
          children: primaryActionText,
          disabled: isPrimaryActionDisabled,
          href: primaryActionHref,
          to: primaryActionTo,
          onClick: onPrimaryActionClick,
        }}
        secondaryActionProps={{
          type: secondaryActionType,
          children: secondaryActionText,
          disabled: isSecondaryActionDisabled,
          onClick: onSecondaryActionClick,
        }}
      />}
    </form>
  </ModalBase>
})

export default FormModal
